<template>
  <div
    :id="formEntryMainId"
    class="flex flex-col relative mb-10 gap-1"
  >
    <!-- Label for the field (rendered at the top of the field depending on which field type it is) -->
    <label
      :id="formEntryLabelId"
      v-if="isLabelRequired()"
      :for="hasSingleControlLabel() ? fieldControlId : undefined"
      class="!text-base text-unify-blue-400"
    >
      {{ props.formField.label }}
    </label>

    <!-- Error message using PrimeVue Message component -->
    <Message
      v-if="props.formField.isValid === false"
      severity="error"
      :closable="false"
      class="!m-0"
    >
      {{ props.formField.errorMessage }}
    </Message>

    <!-- Help text for extra information related to the field -->
    <p
      v-if="props.formField.helpText"
      :id="formEntryHelpTextId"
      class="text-unify-grey-500"
    >
      <i>{{ props.formField.helpText }}</i>
    </p>

    <!-- ********************* -->
    <!-- If field type is TEXT -->
    <!-- ********************* -->
    <div class="field">
      <InputText
        :id="fieldControlId"
        class="w-full"
        v-model="selectedValue as string"
        v-if="props.formField.fieldType === 'text'"
        :aria-describedby="describedByIds"
        :autocomplete="inputAutocomplete"
        :placeholder="props.formField.placeholder"
        :invalid="props.formField.isValid == false"
        @blur="emitBlur"
        :data-test="props.formField.name"
      />
    </div>

    <!-- ********************** -->
    <!-- If field type is EMAIL -->
    <!-- ********************** -->

    <div class="field">
      <InputText
        :id="fieldControlId"
        class="w-full"
        v-model="selectedValue as string"
        v-if="props.formField.fieldType === 'email'"
        :aria-describedby="describedByIds"
        :autocomplete="inputAutocomplete ?? 'email'"
        type="email"
        @blur="emitBlur"
        :invalid="(hasFieldBeenVisited && props.formField.isRequired && isSelectedValueEmpty) || props.formField.isValid == false"
        :data-test="props.formField.name"
      />
    </div>

    <!-- ********************** -->
    <!-- If field type is RADIO -->
    <!-- ********************** -->

    <div
      :id="radioGroupId"
      v-if="props.formField.fieldType === 'radio'"
      role="radiogroup"
      :aria-labelledby="formEntryLabelId"
      class="field w-full flex flex-row flex-wrap gap-4 gap-x-8"
      :data-test="props.formField.name"
    >
      <div
        v-for="choice in props.formField.choices"
        :key="choice[1]"
        class="flex items-center"
      >
        <RadioButton
          v-model="selectedValue"
          :input-id="choiceInputId(choice)"
          :name="`${fieldId}-radio`"
          :value="choice[0]"
          @blur="emitBlur"
          :data-test="choice[0] as string"
        />
        <label
          :for="choiceInputId(choice)"
          class="ml-2 font-regular text-unify-grey-600"
        >
          {{ choice[1] }}
        </label>
      </div>
    </div>

    <!-- ********************** -->
    <!-- If field type is CUSTOMRADIO -->
    <!-- ********************** -->

    <div
      v-if="props.formField.fieldType === 'customradio'"
      class="field w-full flex flex-col gap-2"
    >
      <div
        :id="customRadioGroupId"
        role="radiogroup"
        :aria-labelledby="formEntryLabelId"
        class="w-full flex flex-row flex-wrap gap-4 gap-x-8"
        :data-test="props.formField.name"
      >
        <div
          class="flex items-center"
          v-for="choice in props.formField.choices"
          :key="choice[1]"
        >
          <RadioButton
            v-model="selectedValue"
            :input-id="choiceInputId(choice)"
            :name="`${fieldId}-radio`"
            :value="choice[0]"
            @blur="emitBlur"
            :data-test="choice[0] as string"
          />
          <label
            :for="choiceInputId(choice)"
            class="ml-2 font-regular text-unify-grey-600"
          >
            {{ choice[1] }}
          </label>
        </div>
      </div>
      <FloatLabel
        v-if="props.formField.choices?.length && selectedValue === props.formField.choices[props.formField.choices.length - 1][0]"
        class="w-full mt-2"
        variant="on"
      >
        <InputText
          :id="customChoiceInputId(props.formField.choices[props.formField.choices.length - 1])"
          v-model="customValue"
          :placeholder="props.formField.customPlaceholder"
          :maxlength="props.formField.customMaxLength"
          :invalid="props.formField.customIsValid === false"
          @blur="emitBlur"
          :data-test="(props.formField.choices[props.formField.choices.length - 1][0] as string) + '-custom'"
          :aria-label="props.formField.customPlaceholder"
          class="w-full"
        />
        <label :for="customChoiceInputId(props.formField.choices[props.formField.choices.length - 1])">
          {{ props.formField.customPlaceholder }}
        </label>
      </FloatLabel>
      <Message
        v-if="props.formField.customIsValid === false"
        severity="error"
        :closable="false"
        class="!m-0"
      >
        {{ props.formField.customErrorMessage }}
      </Message>
    </div>

    <!-- ************************* -->
    <!-- If field type is CHECKBOX -->
    <!-- ************************ -->
    <div
      v-if="props.formField.fieldType === 'checkbox'"
      class="field w-full flex items-center mt-2"
    >
      <Checkbox
        :input-id="fieldControlId"
        v-model="selectedValue"
        :binary="true"
        :true-value="true"
        :false-value="false"
        @blur="emitBlur"
        :data-test="props.formField.name"
      />
      <label
        :for="fieldControlId"
        class="ml-2 font-regular text-unify-grey-600"
        v-html="props.formField.label"
      />
      <!-- </label> -->
    </div>

    <!-- ***************************** -->
    <!-- If field type is CHECKBOXGROUP -->
    <!-- ***************************** -->
    <div
      v-if="props.formField.fieldType === 'checkboxgroup'"
      role="group"
      :aria-labelledby="formEntryLabelId"
      class="field w-full flex flex-row flex-wrap gap-4 gap-x-8"
      :data-test="props.formField.name"
    >
      <div
        v-for="choice in props.formField.choices"
        :key="choice[1]"
        class="flex items-center"
      >
        <Checkbox
          v-model="selectedValue"
          :input-id="choiceInputId(choice)"
          :name="`dynamic-checkbox-${props.formField.name}`"
          :value="choice[0]"
          @blur="emitBlur"
          :data-test="choice[0] as string"
        />
        <label
          :for="choiceInputId(choice)"
          class="ml-2 font-regular text-unify-grey-600"
        >
          {{ choice[1] }}
        </label>
      </div>
    </div>

    <!-- ***************************** -->
    <!-- If field type is SELECTBUTTON -->
    <!-- **************************** -->
    <div
      v-if="props.formField.fieldType === 'selectbutton'"
      :class="['field w-full flex flex-col', { 'bg-unify-grey-100': props.formField.choices && props.formField.choices.length > 3 }]"
    >
      <SelectButton
        :id="fieldControlId"
        v-model="selectedValue"
        :aria-labelledby="formEntryLabelId"
        :options="selectButtonOptions as any"
        option-label="label"
        option-value="value"
        @blur="emitBlur"
        class="flex-wrap"
        :data-test="props.formField.name"
      />
    </div>

    <!-- **************************** -->
    <!-- If field type is MULTISELECT -->
    <!-- **************************** -->
    <div
      v-if="props.formField.fieldType === 'multiselect'"
      class="field w-full flex flex-col"
    >
      <!-- The panel stays open after each pick, unlike a single-select, so the header says how to
           finish and the footer's Done button closes it. hide(true) returns focus to the field, and
           @hide still fires so validation runs the same as clicking outside. -->
      <MultiSelect
        ref="multiSelectRef"
        :id="fieldControlId"
        v-model="selectedValue"
        :aria-labelledby="formEntryLabelId"
        :options="props.formField.choices?.map((choice) => ({ label: choice[1], value: choice[0] }))"
        option-label="label"
        option-value="value"
        :placeholder="props.formField.placeholder"
        class=""
        pt:overlay:class="max-w-[calc(100vw-2rem)]"
        pt:option:class="whitespace-normal [&_.p-checkbox]:shrink-0"
        :show-toggle-all="false"
        @hide="emitBlur"
        :data-test="props.formField.name"
      >
        <template #header>
          <p class="px-4 pt-3 pb-1 text-unify-grey-500">
            {{ t('form.helpText.multiSelectPanel') }}
          </p>
        </template>
        <template #footer>
          <div class="flex justify-start p-2">
            <ButtonInput
              :label="t('buttons.done')"
              size="small"
              @click="multiSelectRef?.hide(true)"
              :data-test="`${props.formField.name}-done`"
            />
          </div>
        </template>
      </MultiSelect>
    </div>

    <!-- **************************** -->
    <!-- If field type is DROPDOWN -->
    <!-- **************************** -->
    <div
      v-if="props.formField.fieldType === 'dropdown'"
      class="field w-full flex flex-col gap-3 gap-x-8"
    >
      <!-- input-id puts the id on the role="combobox" span, but a span isn't labelable so <label for>
           alone doesn't name it — aria-labelledby does. append-to="self" keeps the listbox right after
           the combobox in the DOM: teleported to <body>, Narrator's scan-mode arrow keys skip past it
           to the next field. Being in-flow also puts the overlay's hidden role="status" "No selected
           item" span in scan-mode reading order as a dead stop; hide it — the combobox already
           announces its value. Each option gets a data-test of its choice value, matching the per-choice
           hooks on the radio/select-button fields so E2E tests can pick options independent of locale. -->
      <SelectComponent
        :input-id="fieldControlId"
        v-model="selectedValue"
        :aria-labelledby="formEntryLabelId"
        append-to="self"
        :pt="dropdownPt"
        :options="props.formField.choices?.map((choice) => ({ label: choice[1], value: choice[0] }))"
        option-label="label"
        option-value="value"
        :placeholder="props.formField.placeholder"
        class=""
        @blur="emitBlur"
        :data-test="props.formField.name"
      />
      <FloatLabel
        v-if="props.formField.customKey && props.formField.choices?.length && selectedValue === props.formField.choices[props.formField.choices.length - 1][0]"
        class="w-full mt-4"
        variant="on"
      >
        <InputText
          :id="customChoiceInputId(props.formField.choices[props.formField.choices.length - 1])"
          v-model="customValue"
          :placeholder="props.formField.customPlaceholder"
          :maxlength="props.formField.customMaxLength"
          :invalid="props.formField.customIsValid === false"
          @blur="emitBlur"
          :data-test="(props.formField.choices[props.formField.choices.length - 1][0] as string) + '-custom'"
          :aria-label="props.formField.customPlaceholder"
          class="w-full"
        />
        <label :for="customChoiceInputId(props.formField.choices[props.formField.choices.length - 1])">
          {{ props.formField.customPlaceholder }}
        </label>
      </FloatLabel>
      <Message
        v-if="props.formField.customIsValid === false"
        severity="error"
        :closable="false"
        class="!m-0 mt-2"
      >
        {{ props.formField.customErrorMessage }}
      </Message>
    </div>

    <!-- **************************** -->
    <!-- If field type is DATEPICKER -->
    <!-- **************************** -->
    <!-- input-id (not id) puts the id on the <input> itself, so the label's for= and the panel lookups in
         onDatePickerShow reach it; id would land on PrimeVue's wrapper span. The format hint is visible and
         in aria-describedby since the placeholder isn't reliably announced. useAccessibleMonthPicker supplies
         the rest of the screen reader/keyboard fixes (see its header comment). -->
    <div
      v-if="props.formField.fieldType === 'datepicker'"
      class="field w-full flex flex-col gap-1"
    >
      <p
        :id="formEntryDateFormatHintId"
        class="text-unify-grey-500"
      >
        {{ t('form.helpText.dateFormat') }}
      </p>
      <DatePicker
        :input-id="fieldControlId"
        class=""
        v-model="(selectedValue as Date | null)"
        :aria-labelledby="formEntryLabelId"
        :pt="monthPicker.pt.value"
        :append-to="monthPicker.appendTo"
        :placeholder="props.formField.placeholder"
        :show-icon="true"
        :show-on-focus="false"
        :max-date="maxDobDate"
        date-format="mm/yy"
        view="month"
        @mousedown="onDatePickerMousedown"
        @blur="onDatePickerBlur"
        @input="onDatePickerInput"
        @show="onDatePickerShow"
        @hide="onDatePickerHide"
        :data-test="props.formField.name"
      />
    </div>

    <!-- ******************************** -->
    <!-- If field type is BIRTHDATEPICKER -->
    <!-- ******************************** -->
    <!-- Month and year dropdowns; see BirthDatePicker for why. It emits null until both are picked, which
         maps to '' here - the "unanswered" value FormComponent seeds, so its required-field check treats it as
         empty. A too-young pick is flagged right away: a full date in onBirthDateUpdate, a too-recent year on
         its own in onBirthDateTooYoung. -->
    <BirthDatePicker
      v-if="props.formField.fieldType === 'birthdatepicker'"
      class="field"
      :model-value="selectedValue instanceof Date ? selectedValue : null"
      @update:model-value="onBirthDateUpdate"
      :labelled-by="formEntryLabelId"
      :control-id="fieldControlId"
      :name="props.formField.name"
      :minimum-age="props.formField.minimumAge"
      :described-by="describedByIds"
      :invalid="props.formField.isValid === false"
      :data-test="props.formField.name"
      @blur="emitBlur"
      @too-young="onBirthDateTooYoung"
    />
    <!-- Footer help text for extra information related to the field -->
    <!-- eslint-disable vuejs-accessibility/click-events-have-key-events,
         vuejs-accessibility/no-static-element-interactions - this div only delegates clicks from
         the a links rendered by v-html; those links are natively focusable and keyboard
         activation (Enter) fires a click that bubbles here, so keyboard users are covered. -->
    <div
      v-if="props.formField.footerHelpText"
      :id="formEntryFooterHelpTextId"
      class="text-unify-grey-500 text-left"
      v-html="props.formField.footerHelpText"
      @click="handleFooterHelpTextClick"
    />
    <!-- eslint-enable vuejs-accessibility/click-events-have-key-events,
         vuejs-accessibility/no-static-element-interactions -->
  </div>
</template>

<script lang="ts" setup>
import { FieldType, type ChoiceTuple, type IFormField } from '@/schemas/FormField';
import { ref, watch, computed, nextTick, inject, type PropType } from 'vue';
import { useAccessibleMonthPicker } from '@/composables/useAccessibleMonthPicker';
import { getMinimumAgeThresholdDate, isOverMinimumAge } from '@/services/GeneralService';

defineOptions({ inheritAttrs: false });

const t: any = inject('t');

const props = defineProps({
  formField: {
    type: Object as PropType<IFormField>,
    required: true,
  },
});

const fieldId = computed(() => props.formField.name);

// 1.3.5: maps field names to autocomplete tokens; a field can override via `formField.autocomplete`.
// `dob` is deliberately absent — it renders as a (birth)datepicker, so the attribute never reaches an
// element that would honour it.
const inputAutocomplete = computed(() => {
  if (props.formField.autocomplete) return props.formField.autocomplete;
  switch (props.formField.name) {
    case 'firstName':
      return 'given-name';
    case 'lastName':
      return 'family-name';
    case 'email':
      return 'email';
    case 'zipCode':
      return 'postal-code';
    default:
      return undefined;
  }
});

const describedByIds = computed(() => {
  const ids: string[] = [];
  if (props.formField.helpText) ids.push(formEntryHelpTextId.value);
  if (props.formField.footerHelpText) ids.push(formEntryFooterHelpTextId.value);
  return ids.length > 0 ? ids.join(' ') : undefined;
});

// The latest date a user can pick and still meet the field's minimumAge, if any
const maxDobDate = computed(() =>
  props.formField.minimumAge
    ? getMinimumAgeThresholdDate(props.formField.minimumAge)
    : undefined,
);

function choiceInputId(choice: ChoiceTuple) {
  return `${props.formField.name}-${String(choice[0])}`;
}

function customChoiceInputId(choice: ChoiceTuple) {
  return `${props.formField.name}-${String(choice[0])}-custom`;
}

// update:formField asks the parent (which owns the field object) to merge validation state into it
const emits = defineEmits([
  'update:modelValue',
  'update:modelCustomValue',
  'update:formField',
  'validateEmailEvent',
  'formEntryBlur',
  'formEntryInput',
]);
const selectedValue = ref(props.formField.value);
const customValue = ref('');
const isSelectedValueEmpty = ref(selectedValue.value === '');
const isCustomValueEmpty = ref(customValue.value === '');
const hasFieldBeenVisited = ref(false);
const multiSelectRef = ref<{ hide: (isFocus?: boolean) => void } | null>(null);
const suppressDatePickerBlur = ref(false);
const datePickerPanelOpen = ref(false);
const pendingDatePickerBlur = ref(false);
let datePickerPanelEl: HTMLElement | null = null;
const MONTH_YEAR_PATTERN = /^(0[1-9]|1[0-2])\/(\d{4})$/;
// Set right before a manual, precisely-timed emit (see clearDobModel) so the watcher below
// doesn't also emit once it eventually runs - that later emit would race the too-young flag
let suppressNextSelectedValueEmit = false;

const fieldControlId = computed(() => `form-entry-control-${props.formField.name}`);
const formEntryMainId = computed(() => `form-entry-main-${props.formField.name}`);
const formEntryLabelId = computed(() => `form-entry-label-${props.formField.name}`);
const formEntryHelpTextId = computed(() => `form-entry-helptext-${props.formField.name}`);
const formEntryFooterHelpTextId = computed(() => `form-entry-footerhelptext-${props.formField.name}`);
const formEntryDateFormatHintId = computed(() => `form-entry-dateformathint-${props.formField.name}`);
const radioGroupId = computed(() => `radio-group-${props.formField.name}`);
const customRadioGroupId = computed(() => `custom-radio-group-${props.formField.name}`);

const handleFooterHelpTextClick = (event: MouseEvent) => {
  if (!props.formField.onClick) {
    return;
  }

  const target = event.target as HTMLElement;
  if (!target.closest('a')) {
    return;
  }

  event.preventDefault();
  props.formField.onClick();
};

// If field Type is select button, map the choices to the options
const selectButtonOptions = computed(() => {
  if (props.formField.fieldType === FieldType.BASICSELECTBUTTON && props.formField.choices) {
    return props.formField.choices.map((choice) => ({
      label: choice[1],
      value: choice[0],
    }));
  } else {
    return null;
  }
});

// Passthrough for the dropdown's Select (see the template comment there for why each is needed)
const dropdownPt = {
  hiddenSelectedMessage: { 'aria-hidden': 'true' },
  option: ({ context }: { context: { option: { value: string } } }) => ({ 'data-test': context.option.value }),
};

// Accessibility fixes for the datepicker; its input is described by the format hint plus any help text
const monthPicker = useAccessibleMonthPicker(fieldControlId, () =>
  [formEntryDateFormatHintId.value, describedByIds.value].filter(Boolean).join(' '),
);

// Watch the selected value ref and emit an event when it changes
watch(selectedValue, (newValue) => {
  if (suppressNextSelectedValueEmit) {
    // Already emitted manually with precise timing (see clearDobModel) - emitting again here,
    // after the too-young flag is set, would trigger the parent's revalidate-on-change and
    // overwrite the too-young message with a generic "required" one
    suppressNextSelectedValueEmit = false;
  } else {
    emits('update:modelValue', newValue);
  }
  if (newValue === '') {
    isSelectedValueEmpty.value = true;
  } else {
    isSelectedValueEmpty.value = false;
  }

  // Switching away from the choice that reveals the "please specify" input hides it, but its
  // value would otherwise stay in formValues (silently failing the full-schema validate() on
  // submit) and any stale invalid state would keep showing an error for a field the user can no
  // longer see. Clearing customValue here also emits update:modelCustomValue below, syncing the
  // parent's formValues entry back to ''.
  const lastChoiceValue = props.formField.choices?.length
    ? props.formField.choices[props.formField.choices.length - 1][0]
    : undefined;
  if (props.formField.customKey && newValue !== lastChoiceValue) {
    customValue.value = '';
    emits('update:formField', {
      customIsValid: undefined,
      customErrorMessage: undefined,
    });
  }
});

// Watch the custom value ref if it exists and emit an event when it changes
watch(customValue, (newValue) => {
  emits('update:modelCustomValue', newValue);
  if (newValue === '') {
    isCustomValueEmpty.value = true;
  } else {
    isCustomValueEmpty.value = false;
  }
});

function emitBlur() {
  hasFieldBeenVisited.value = true;
  emits('formEntryBlur');
}

function onDatePickerMousedown() {
  suppressDatePickerBlur.value = true;
  setTimeout(() => {
    suppressDatePickerBlur.value = false;
  }, 0);
}

// Flags the field as invalid with the same message used for a too-young dob on submit,
// so clicking a disabled calendar cell, typing an out-of-range date, or making a birthdatepicker pick
// too young by changing its year isn't silently ignored
function flagDobTooYoung() {
  emits('update:formField', {
    isValid: false,
    errorMessage: t('validation.dobTooYoung', { age: props.formField.minimumAge }),
  });
}

// Commits the typed too-young dob to selectedValue (and therefore the parent's formValues)
// instead of clearing it, so the too-young date stays put and the parent's Continue button can
// react to it (and stay disabled) rather than the field just going blank and looking answerable.
// Emits manually, before flagDobTooYoung runs, while field.isValid is still whatever it was
// before - the parent only revalidates-on-change when isValid is already false, so this ordering
// avoids that revalidation stomping the too-young message with a generic "required" one.
// suppressNextSelectedValueEmit then stops the reactive watcher (which fires later, by which
// point isValid IS false) from emitting a second, badly-timed update for the same change.
function setTooYoungDobModel(typedDate: Date) {
  suppressNextSelectedValueEmit = true;
  selectedValue.value = typedDate;
  emits('update:modelValue', typedDate);
}

// BirthDatePicker offers too-young dates rather than hiding them - flag one with the too-young message
// straight away instead of waiting for submit.
// setTooYoungDobModel must run before flagDobTooYoung; see its comment.
function onBirthDateUpdate(value: Date | null) {
  if (value && props.formField.minimumAge && !isOverMinimumAge(value, props.formField.minimumAge)) {
    setTooYoungDobModel(value);
    flagDobTooYoung();
    return;
  }
  selectedValue.value = value ?? '';
}

// BirthDatePicker reports too-young picks before they're a full date too (a too-recent year with no month yet,
// which never reaches the model). Record that on the field so FormComponent can block Continue and keep the
// too-young message through validation, and flag it now. FormComponent re-validates once it turns off.
function onBirthDateTooYoung(tooYoung: boolean) {
  emits('update:formField', { isTooYoung: tooYoung });
  if (tooYoung) flagDobTooYoung();
}

// PrimeVue's max-date only stops in-range selection; clicking a disabled month cell is otherwise a no-op.
// Disabled cells get pointer-events: none, so they never become event.target - check click coordinates
// against each disabled cell's rect instead.
function onDatePickerPanelClick(event: MouseEvent) {
  if (!maxDobDate.value || !datePickerPanelEl) return;
  const disabledCells = datePickerPanelEl.querySelectorAll('[data-p-disabled="true"]');
  for (let i = 0; i < disabledCells.length; i++) {
    const rect = disabledCells[i].getBoundingClientRect();
    if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
      flagDobTooYoung();
      return;
    }
  }
}

function removeDatePickerPanelClickHandler() {
  datePickerPanelEl?.removeEventListener('click', onDatePickerPanelClick);
  datePickerPanelEl = null;
}

// A manually-typed out-of-range date is rejected by PrimeVue without emitting a model update,
// so the only signal available is the raw input event - check it directly once it looks complete
function onDatePickerInput(event: Event) {
  if (!maxDobDate.value) return;
  const input = event.target as HTMLInputElement;
  const match = input.value.match(MONTH_YEAR_PATTERN);
  if (!match) return;
  const typedDate = new Date(Number(match[2]), Number(match[1]) - 1, 1);
  if (typedDate > maxDobDate.value) {
    // The typed value itself was never committed to the model by PrimeVue (see comment above) -
    // commit it ourselves so it stays put instead of leaving a stale or empty value behind.
    // setTooYoungDobModel must run before flagDobTooYoung; see its comment.
    setTooYoungDobModel(typedDate);
    flagDobTooYoung();
  }
}

function onDatePickerShow() {
  datePickerPanelOpen.value = true;
  monthPicker.onShow();
  nextTick(() => {
    const panel = monthPicker.getPanel();
    if (!panel) return;
    removeDatePickerPanelClickHandler();
    datePickerPanelEl = panel;
    panel.addEventListener('click', onDatePickerPanelClick);
  });
}

function onDatePickerHide() {
  monthPicker.onHide();
  removeDatePickerPanelClickHandler();
  datePickerPanelOpen.value = false;
  if (pendingDatePickerBlur.value) {
    pendingDatePickerBlur.value = false;
    setTimeout(() => emitBlur(), 0);
  }
}

function onDatePickerBlur() {
  if (suppressDatePickerBlur.value) return;
  if (datePickerPanelOpen.value) {
    pendingDatePickerBlur.value = true;
    return;
  }
  emitBlur();
}

function isLabelRequired() {
  if (
    props.formField.fieldType === 'text' ||
    props.formField.fieldType === 'email' ||
    props.formField.fieldType === 'radio' ||
    props.formField.fieldType === 'customradio' ||
    props.formField.fieldType === 'selectbutton' ||
    props.formField.fieldType === 'multiselect' ||
    props.formField.fieldType === 'checkboxgroup' ||
    props.formField.fieldType === 'datepicker' ||
    props.formField.fieldType === 'birthdatepicker' ||
    props.formField.fieldType === 'dropdown'
  )
    return true;
  return false;
}

function hasSingleControlLabel() {
  if (props.formField.fieldType === 'text' || props.formField.fieldType === 'email' || props.formField.fieldType === 'selectbutton' || props.formField.fieldType === 'multiselect' || props.formField.fieldType === 'datepicker' || props.formField.fieldType === 'dropdown') return true;
  return false;
}
</script>

<style scoped lang="scss">
h1,
h2,
h3,
h4,
h5,
h6 {
  margin: 0;
  padding: 0;
}
</style>
